import type { JoinPercentageOption } from 'app/src/model'
import { membershipApi } from 'app/src/services/welfareportal'
import { calculateSimulationInfo, euroFormatterIt } from 'app/src/shared/helpers'

import { useTranslation, useWebAppContext } from 'app/src/shared/hooks'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { Slider } from 'primereact/slider'
import { useEffect, useState, type Dispatch, type ReactElement, type SetStateAction } from 'react'

export function ConfirmPercentageDialog(props: {
  visible: boolean
  setChosenPercentage: Dispatch<SetStateAction<number>>
  chosenPercentage: number
  membershipData: membershipApi.MembershipExtended
  onCancel: () => void
  onConfirm: () => void
  percentages?: number[]
}): ReactElement {
  const context = useWebAppContext()
  const { t } = useTranslation('joinFunnel', { keyPrefix: 'JoinFunnel.FirstStep.choosePercentageDialog' })
  const tCommon = useTranslation('joinFunnel', { keyPrefix: 'JoinFunnel.Common' })

  /*const membershipResponse = membershipApi.useUserMembershipActiveGet<membershipApi.Membership>({
    pathParams: { userId: context.userInfo.userId }
  })*/

  const [percentages, setPercentages] = useState<JoinPercentageOption[]>()
  const [conversioneInCredito, setConversioneInCredito] = useState<number>(props.chosenPercentage)
  const [totaleNetto, setTotaleNetto] = useState<number | undefined>()
  const [integrazionePoste, setIntegrazionePoste] = useState<number | undefined>()
  const [importoBustaPaga, setImportoBustaPaga] = useState<number | undefined>()
  const [importoNettoConvertitoInWelfare, setImportoNettoConvertitoInWelfare] = useState<number | undefined>()

  useEffect(() => {
    if (context.portalConfiguration.joinConfig !== undefined) {
      const percToInject: JoinPercentageOption[] = props.percentages
        ? props.percentages.map((p) => ({ key: (p * 100).toString() + '%', value: p }))
        : context.portalConfiguration.joinConfig.joinPercentages.map((p) => ({ key: (p * 100).toString() + '%', value: p }))
      context.portalConfiguration.joinConfig?.joinPercentages.map((p) => ({
        key: (p * 100).toString() + '%',
        value: p,
      }))
      setPercentages(percToInject)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.percentages])

  useEffect(() => {
    if (conversioneInCredito !== undefined) {
      calcPlus(conversioneInCredito)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversioneInCredito])

  function calcPlus(selectedPercentage: number) {
    const activeMembership = props.membershipData
    if (activeMembership !== undefined) {
      if (activeMembership.membershipInfo.bonusAmount && activeMembership.membershipInfo.bonusAmount > 0) {
        const calc = calculateSimulationInfo(selectedPercentage, activeMembership)

        if (calc !== undefined) {
          setImportoBustaPaga(calc.importoBustaPaga)
          setIntegrazionePoste(calc.integrazionePoste)
          setTotaleNetto(calc.totaleNetto)
          setImportoNettoConvertitoInWelfare(calc.conversioneInCredito + calc.integrazionePoste)
        }
      }
    }
  }

  const choiceMode = () => {
    switch (context.userInfo.welfareCampaign.configuration.joinConfig?.choiceMode) {
      case 'slider':
        return (
          <>
            <div className='relative mb-8 w-[90%] lg:mb-0'>
              <Slider
                value={conversioneInCredito}
                min={0}
                max={1}
                step={0.001}
                onChange={(e) => {
                  if (typeof e.value === 'number') setConversioneInCredito(parseFloat(e.value.toFixed(3)))
                }}
              />
              <div
                className='absolute rounded bg-[var(--primary-color)] px-2 py-1 text-center text-xs font-semibold text-white'
                style={{
                  left: `calc(${conversioneInCredito * 100}% - 10px)`,
                  top: '15px',
                }}
              >
                {(conversioneInCredito * 100).toFixed(1)}%
              </div>
            </div>
          </>
        )
      case 'input':
        return <></>
      case 'combo':
      default:
        return (
          <Dropdown
            value={conversioneInCredito}
            onChange={(e) => setConversioneInCredito(e.value)}
            options={percentages}
            optionLabel='key'
            placeholder='Seleziona una percentuale'
            className='w-64'
          />
        )
    }
  }

  return (
    <Dialog
      header={t('title')}
      className='w-2/3'
      visible={props.visible}
      onHide={() => {
        props.onCancel()
      }}
    >
      <div className='grid gap-4'>
        <div>{t('text')}</div>
        <div className='font-semibold text-[var(--primary-color)]'>{t('estimateText')}</div>
        <div className='text-[50px] font-medium'>{euroFormatterIt.format(props.membershipData.membershipInfo.bonusAmount)}</div>
        <div className='grid gap-4 lg:grid-cols-2'>
          <div>
            <div className='h-12'>{t('percentageDropdownText')}</div>
            <div>{choiceMode()}</div>
          </div>
          <div>
            <div className='h-12 font-semibold'>{t('additionalCreditText')}</div>
            <div className='text-[36px] font-medium' id='idConversioneInCredito'>
              {integrazionePoste !== undefined ? '+' + euroFormatterIt.format(integrazionePoste) : tCommon.t('notAvailableText')}
            </div>
          </div>
        </div>
        <div className='grid grid-rows-2'>
          <div className='rounded-tl-lg rounded-tr-lg border-l border-r border-t border-[var(--primary-color)] p-4 text-center'>
            <div className='mb-2 font-semibold text-[var(--primary-color)]'>{t('convertTitle')}</div>
            <div>{t('convertEstimateText')}</div>
            <div id='totaleNetto' className='text-[36px] font-semibold text-[var(--primary-color)]'>
              {totaleNetto !== undefined ? euroFormatterIt.format(totaleNetto) : tCommon.t('notAvailableText')}
            </div>
          </div>
          <div className='grid grid-cols-2 gap-4 rounded-bl-lg rounded-br-lg border border-[var(--primary-color)] bg-[var(--card-background)] p-4'>
            <div>
              <Card className='h-full flex-grow text-center'>
                <div>{t('convertCard.text')}</div>
                <div className='font-semibold' id='idTotaleNetto'>
                  {importoNettoConvertitoInWelfare !== undefined
                    ? euroFormatterIt.format(importoNettoConvertitoInWelfare)
                    : tCommon.t('notAvailableText')}
                </div>
              </Card>
            </div>
            <div>
              <Card className='h-full text-center'>
                <div>{t('doNotConvertCard.text')}</div>
                <div className='font-semibold' id='in_busta'>
                  {importoBustaPaga !== undefined ? euroFormatterIt.format(importoBustaPaga) : tCommon.t('notAvailableText')}
                </div>
              </Card>
            </div>
          </div>
        </div>
        <div className='text-center'>
          <Button outlined label={t('abortBtnLabel')} rounded className='mr-4' onClick={props.onCancel}></Button>
          <Button
            label={t('confirmBtnLabel')}
            onClick={() => {
              props.setChosenPercentage(conversioneInCredito)
              props.onConfirm()
            }}
            rounded
          ></Button>
        </div>
      </div>
    </Dialog>
  )
}
